import React, { useMemo, useEffect } from 'react';

import {
  TextInput,
  Button,
  Stack,
  Group,
  Select,
  Switch
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { type Caller } from './types';
import { EMAIL_REGEX } from '../../utils/regex';
import { PostJSON } from '../../utils/request';

export interface CallerFormData {
  firstName: string;
  lastName: string;
  email: string;
  type: string | null;
  isOptedOut: boolean;
  phone?: string;
}

export interface CallerFormProps {
  initialData?: Caller;
  onSuccess?: () => void;
  onCancel?: () => void;
  mode?: 'add' | 'edit';
}

interface CallerResponse {
  success: boolean;
  caller: Caller;
}

const CallerForm: React.FC<CallerFormProps> = ({
  initialData,
  onSuccess,
  onCancel,
  mode = 'add'
}) => {
  // Format phone number to +1XXXXXXXXXX format
  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return phone;
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    
    if (digitsOnly.length === 10) {
      // 10-digit number: add +1 prefix
      return `+1${digitsOnly}`;
    }
    
    // Return original if not a valid format
    return phone;
  };

  const form = useForm<CallerFormData>({
    initialValues: {
      firstName: initialData?.firstName || '',
      lastName: initialData?.lastName || '',
      email: initialData?.email || '',
      type: initialData?.type || null,
      isOptedOut: initialData?.isOptedOut ?? false,
      phone: mode === 'add' ? '' : undefined
    },
    validate: {
      firstName: (value) => {
        if (mode === 'add') {
          if (!value || value.trim().length === 0) return 'First name is required';
        }
        return null;
      },
      lastName: (value) => {
        if (mode === 'add') {
          if (!value || value.trim().length === 0) return 'Last name is required';
        }
        return null;
      },
      email: (value) => {
        if (!value) return null; // Email is optional
        return !EMAIL_REGEX.test(value) ? 'Invalid email format' : null;
      },
      phone: (value) => {
        if (mode === 'add') {
          if (!value) return 'Phone number is required';
          // Remove all non-digit characters for validation
          const digitsOnly = value.replace(/\D/g, '');
          // Check if it's exactly 10 digits
          if (digitsOnly.length === 10) {
            return null; // Valid 10-digit number
          } else {
            return 'Please enter a valid US phone number (10 digits)';
          }
        }
        return null; // Phone is not required in edit mode
      },
      type: (value) => {
        if (!value) return 'Type is required';
        return null;
      }
    }
  });

  // Update form when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      form.setValues({
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        email: initialData.email || '',
        type: initialData.type || null,
        isOptedOut: initialData.isOptedOut ?? false,
        phone: mode === 'add' ? '' : undefined
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  const typeOptions = useMemo(
    () => [
      { label: 'Family', value: 'family' },
      { label: 'Aide', value: 'aide' },
      { label: 'Other', value: 'other' }
    ],
    []
  );

  const handleSubmit = async (values: CallerFormData) => {
    try {
      if (mode === 'edit' && initialData?.id) {
        // Edit existing caller
        await PostJSON<CallerResponse, { id: number; firstName: string; lastName: string; email: string; type: string | null; isOptedOut: boolean }>(
          '/api/caller/edit',
          {
            id: initialData.id,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            type: values.type || null,
            isOptedOut: values.isOptedOut
          }
        );
        notifications.show({
          title: 'Success',
          message: 'Caller updated successfully',
          color: 'green'
        });
      } else {
        // Add new caller
        if (!values.phone) {
          notifications.show({
            title: 'Error',
            message: 'Phone number is required',
            color: 'red'
          });
          return;
        }
        await PostJSON<CallerResponse, { firstName: string; lastName: string; email: string; type: string | null; isOptedOut: boolean; phone: string }>(
          '/api/caller/create',
          {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            type: values.type || null,
            isOptedOut: values.isOptedOut,
            phone: formatPhoneNumber(values.phone)
          }
        );
        notifications.show({
          title: 'Success',
          message: 'Caller created successfully',
          color: 'green'
        });
        form.reset();
      }
      onSuccess?.();
    } catch (e) {
      notifications.show({
        title: 'Error',
        message: e instanceof Error ? e.message : `Failed to ${mode === 'edit' ? 'update' : 'create'} caller`,
        color: 'red'
      });
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="lg">
        <TextInput
          label="First Name"
          placeholder="Enter first name"
          required={mode === 'add'}
          {...form.getInputProps('firstName')}
        />
        <TextInput
          label="Last Name"
          placeholder="Enter last name"
          required={mode === 'add'}
          {...form.getInputProps('lastName')}
        />
        <TextInput
          label="Email"
          placeholder="Enter email"
          {...form.getInputProps('email')}
        />
        {mode === 'add' && (
          <TextInput
            required
            label="Phone (Not Editable After Creation)"
            placeholder="Enter phone number"
            {...form.getInputProps('phone')}
          />
        )}
        <Select
          required
          data={typeOptions}
          label="Type"
          placeholder="Select type"
          {...form.getInputProps('type')}
        />
        <Switch
          description="When enabled, this caller will not receive automated check-in reminders"
          label="Opt Out"
          {...form.getInputProps('isOptedOut', { type: 'checkbox' })}
        />
        {mode === 'edit' && initialData?.configs?.visitUpdatesEnabled !== undefined && (
          <Switch
            checked={initialData.configs.visitUpdatesEnabled === true}
            description="This preference is set by the caller during a check-in call and cannot be edited here"
            disabled
            label="Visit Updates"
          />
        )}

        <Group justify="flex-end">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit">
            {mode === 'edit' ? 'Save Changes' : 'Create Caller'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default CallerForm;

